import { useState } from 'react';
import { api, type CertificateStatus, type EnvironmentDomain } from '../api';
import { useResource } from '../hooks';
import { CopyButton } from './CopyButton';
import { Checkbox, Table } from './primitives';
import { ErrorNotice, StatusBadge } from './ui';

/**
 * What a hostname's certificate is, as data (GAP-21): its state, when it
 * expires, who issued it, whether it is a staging certificate, and — when a
 * CDN hides the server's own certificate — what the server holds, apart from
 * what visitors get. The rest of this file is the controls that belong to it:
 * automatic issuance and the staging CA, and the DNS records a hostname needs.
 */

const DAY_MS = 86_400_000;

/** A certificate with this many days left or fewer is shown as close to its end. */
const SOON_DAYS = 7;

function daysClass(days: number | null): string {
  if (days === null) return 'cert-cell__days';
  if (days < 0) return 'cert-cell__days cert-cell__days--over';
  return days <= SOON_DAYS ? 'cert-cell__days cert-cell__days--soon' : 'cert-cell__days';
}

/** Whole days until an instant; negative once it has passed. */
export function daysLeft(iso: string, now = Date.now()): number {
  return Math.floor((Date.parse(iso) - now) / DAY_MS);
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function shortTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** A staging certificate is installed but never "Issued": browsers reject it. */
export function certificateBadgeValue(d: Pick<EnvironmentDomain, 'certificate_status' | 'certificate_staging'>): CertificateStatus | 'staging' {
  return d.certificate_staging && d.certificate_status === 'issued' ? 'staging' : d.certificate_status;
}

export function CertificateCell({ domain: d }: { domain: EnvironmentDomain }) {
  if (d.tls_mode === 'none') return <StatusBadge kind="tls" value="not_applicable" variant="plain" />;
  if (d.tls_mode === 'external') return <span className="muted">Terminated in front</span>;
  const days = d.certificate_not_after ? daysLeft(d.certificate_not_after) : null;
  const originDays = d.origin_cert_not_after ? daysLeft(d.origin_cert_not_after) : null;
  return (
    <div className="cert-cell" data-testid={`certificate-${d.hostname}`}>
      <StatusBadge kind="certificate" value={certificateBadgeValue(d)} title={d.last_certificate_error || undefined} />
      {d.certificate_not_after ? (
        <div className="table__sub cert-cell__expiry">
          <time dateTime={d.certificate_not_after}>{shortDate(d.certificate_not_after)}</time>
          {' · '}
          <span className={daysClass(days)}>{days !== null && days < 0 ? 'expired' : `${days} d`}</span>
          {d.certificate_issuer ? <> · <span translate="no">{d.certificate_issuer}</span></> : null}
        </div>
      ) : null}
      {d.origin_cert_status ? (
        <div className="table__sub cert-cell__origin" data-testid={`origin-certificate-${d.hostname}`}>
          Server <StatusBadge kind="originCertificate" value={d.origin_cert_status} variant="plain" />
          {originDays !== null && d.origin_cert_status === 'valid' ? <> · <span className={daysClass(originDays)}>{originDays} d</span></> : null}
        </div>
      ) : null}
    </div>
  );
}

/** True when a certificate has been issued (or failed to renew), so renewing makes sense. */
export function hasRenewableCertificate(domains: EnvironmentDomain[]): boolean {
  return domains.some((d) => d.tls_mode === 'acme' && (d.certificate_status === 'issued' || d.certificate_status === 'renewal_failed' || d.certificate_status === 'expired'));
}

/** True when an ACME hostname still has no certificate that browsers accept. */
export function needsCertificate(domains: EnvironmentDomain[]): boolean {
  return domains.some((d) => d.tls_mode === 'acme' && (d.certificate_status === 'none' || d.certificate_status === 'placeholder' || d.certificate_status === 'issue_failed' || (d.certificate_status === 'issued' && d.certificate_staging)));
}

/**
 * Automatic first issuance and the staging CA: the two switches an operator
 * owns, with where the automation stands as data. While the settings load
 * it only keeps its space; when they cannot be read it says so with a retry
 * (an error must not look like a feature that is absent).
 */
export function CertificateAutomation({ environmentId, reloadToken = 0, onChanged }: { environmentId: string; reloadToken?: number; onChanged?: () => void }) {
  const settings = useResource(() => api.getEnvironmentTLSSettings(environmentId), [environmentId, reloadToken], undefined, { key: `env:${environmentId}:tls-settings` });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const s = settings.data?.settings;
  // Space is kept while the settings load, so the table does not jump when they arrive.
  if (!s) {
    if (settings.loading) return <div className="cert-automation cert-automation--loading" aria-hidden="true" />;
    if (settings.error) return <ErrorNotice error={settings.error} onRetry={settings.reload} />;
    return null;
  }

  async function save(next: { auto_issue: boolean; staging: boolean }) {
    setSaving(true);
    setError(null);
    try {
      const res = await api.setEnvironmentTLSSettings(environmentId, next);
      settings.mutate?.(res);
      settings.reload();
      onChanged?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="cert-automation" data-testid="certificate-automation">
      <div className="cert-automation__controls">
        <Checkbox
          label="Issue automatically"
          checked={s.auto_issue}
          disabled={saving}
          onChange={(e) => void save({ auto_issue: e.target.checked, staging: s.staging })}
          data-testid="tls-auto-issue"
        />
        <Checkbox
          label="Use the staging CA"
          checked={s.staging}
          disabled={saving}
          onChange={(e) => void save({ auto_issue: s.auto_issue, staging: e.target.checked })}
          data-testid="tls-staging"
        />
      </div>
      <dl className="cert-automation__facts">
        {s.auto_issue ? (
          <div>
            <dt>Automatic issuance</dt>
            <dd>
              <StatusBadge kind="autoIssue" value={s.global_enabled ? s.status : 'off'} title={s.global_enabled ? undefined : 'Turned off for the whole platform'} />
              {!s.global_enabled ? <span className="muted"> Platform switch off</span> : null}
            </dd>
          </div>
        ) : null}
        {s.auto_issue && s.global_enabled && s.last_attempt_at ? (
          <div>
            <dt>Last attempt</dt>
            <dd><time dateTime={s.last_attempt_at}>{shortTime(s.last_attempt_at)}</time></dd>
          </div>
        ) : null}
        {s.auto_issue && s.global_enabled && s.next_attempt_at && s.status !== 'idle' ? (
          <div>
            <dt>Next attempt</dt>
            <dd><time dateTime={s.next_attempt_at}>{shortTime(s.next_attempt_at)}</time></dd>
          </div>
        ) : null}
        {s.blocked_until ? (
          <div>
            <dt>No requests before</dt>
            <dd><time dateTime={s.blocked_until}>{shortTime(s.blocked_until)}</time></dd>
          </div>
        ) : null}
      </dl>
      {s.auto_issue && s.global_enabled && s.last_error ? <p className="cert-automation__error">{s.last_error}</p> : null}
      <ErrorNotice error={error} />
    </div>
  );
}

const dnsStatusKinds = new Set(['ok', 'missing', 'mismatch', 'unknown']);

function DnsRecordsTable({ environmentId }: { environmentId: string }) {
  const records = useResource(() => api.getDNSRequirements(environmentId), [environmentId], undefined, { key: `env:${environmentId}:dns-requirements`, staleMs: 10_000 });
  if (records.error) return <ErrorNotice error={records.error} onRetry={records.reload} />;
  const rows = records.data?.records ?? [];
  if (!records.data) return <p className="quiet-empty">Checking DNS</p>;
  if (rows.length === 0) return <p className="quiet-empty">No hostnames</p>;
  return (
    <Table label="DNS records" compact>
      <thead>
        <tr>
          <th scope="col">Hostname</th>
          <th scope="col">Record</th>
          <th scope="col">Needed</th>
          <th scope="col">Now</th>
          <th scope="col">DNS</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.hostname}-${r.type}`} data-testid={`dns-record-${r.hostname}`}>
            <td><code translate="no">{r.hostname}</code></td>
            <td>{r.type}</td>
            <td>
              {r.value ? (
                <span className="dns-record__value">
                  <code translate="no">{r.value}</code>
                  <CopyButton value={r.value} label={`${r.hostname} address`} />
                </span>
              ) : (
                <span className="muted">Not known</span>
              )}
            </td>
            <td>{r.observed ? <code translate="no">{r.observed}</code> : <span className="muted">None</span>}</td>
            <td><StatusBadge kind="dns" value={dnsStatusKinds.has(r.status) ? r.status : 'unknown'} /></td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** The records each hostname needs against what public DNS answers now; read when opened. */
export function DnsRecords({ environmentId }: { environmentId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="dns-records" onToggle={(e) => setOpen(e.currentTarget.open)} data-testid="dns-records">
      <summary>DNS records</summary>
      {open ? <DnsRecordsTable environmentId={environmentId} /> : null}
    </details>
  );
}
